import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import FleetVehicleSelect from "./FleetVehicleSelect";
import {
  vmPageShell,
  vmTableScrollWrap,
  vmRecordRowBg,
  getVmRecordTableStyle,
  getVmRecordThStyle,
  getVmRecordTdStyle,
  vmRecordColGroupReact,
} from "./vehicleManagementLayout";

const BASE_URL = API_BASE_URL;

/** Same buckets as Truck Maintenance — vehicle expenses are tagged with `category`. */
const VEHICLE_EXPENSE_CATEGORIES = [
  "Regular Maintenance",
  "Oil Service",
  "Tyre Service",
  "Battery Service",
  "RTO / Document Expense",
  "Spare Parts Service",
  "Inventory Service",
  "Loan",
];

const EXPENSE_TABS = ["All", ...VEHICLE_EXPENSE_CATEGORIES, "Others"];

/** Stable option value for picker (backend may omit _id). */
function truckMaintPickKey(rec, idx) {
  if (rec._id != null) return String(rec._id);
  if (rec.id != null) return String(rec.id);
  return `m-${idx}-${String(rec.truckNo || "")}-${String(rec.date || "")}-${String(rec.amount ?? "")}`;
}

/** Map a Truck Maintenance GET row onto the vehicle expense form shape. */
function mapTruckMaintenanceToExpense(rec) {
  let ymd = new Date().toISOString().split("T")[0];
  const d = rec.date;
  if (d) {
    if (typeof d === "string") {
      ymd = d.includes("T") ? d.slice(0, 10) : d.slice(0, Math.min(10, d.length)) || ymd;
    } else {
      const t = new Date(d).getTime();
      if (!Number.isNaN(t)) ymd = new Date(d).toISOString().split("T")[0];
    }
  }
  const vendor =
    [
      rec.supplierPartyName,
      rec.workshopName,
      rec.serviceCentre,
      rec.rtoEntityName,
      rec.financer === "Other" ? rec.financerOther : rec.financer,
    ]
      .map((x) => (typeof x === "string" ? x.trim() : x))
      .find(Boolean) || "";

  const pay = String(rec.paymentMode || "").trim();
  let paymentMethod = "Cash";
  if (pay === "UPI" || pay === "OTP") paymentMethod = "UPI";
  else if (pay === "Card") paymentMethod = "Card";
  else if (pay === "Credit") paymentMethod = "Bank Transfer";
  else if (pay === "Cash") paymentMethod = "Cash";

  const remarks = typeof rec.remarks === "string" ? rec.remarks.trim() : "";
  const mt = String(rec.maintenanceType || "").trim();
  const descParts = [mt, remarks].filter(Boolean);
  const description = descParts.length ? descParts.join(" — ") : "Truck maintenance";

  const requested =
    [rec.driver, rec.approvedBy, rec.oilApprovedBy].map((x) => String(x || "").trim()).find(Boolean) || "";

  const ref = rec.paymentTxnRef;
  const paymentReference = ref !== undefined && ref !== null && ref !== "" ? String(ref) : "";

  let amountSrc = rec.amount;
  if (mt === "Loan" && rec.loanAmount != null && rec.loanAmount !== "") amountSrc = rec.loanAmount;

  let amountStr = "";
  if (amountSrc !== undefined && amountSrc !== null && amountSrc !== "") {
    const n = parseFloat(amountSrc);
    amountStr = Number.isFinite(n) ? String(amountSrc) : String(amountSrc);
  }

  return {
    date: ymd,
    vehicleNumber: String(rec.truckNo || "").trim(),
    description,
    amount: amountStr,
    requestedBy: requested,
    paidBy: "",
    paymentMethod,
    paymentReference,
    vendor,
    category: mt,
    expenseType: "Vehicle Expense",
    checkAmount: "",
  };
}

function expenseDateKeyYmd(d) {
  if (d == null || d === "") return "";
  if (typeof d === "string") {
    const s = d.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const parsed = Date.parse(s);
    if (!Number.isNaN(parsed)) return new Date(parsed).toISOString().slice(0, 10);
    return "";
  }
  const t = new Date(d).getTime();
  return Number.isNaN(t) ? "" : new Date(t).toISOString().slice(0, 10);
}

function vehicleExpenseFingerprint(r) {
  const raw = parseFloat(String(r.amount ?? "").replace(/,/g, ""));
  const amtNorm = Number.isFinite(raw) ? Math.round(raw * 100) / 100 : 0;
  return [
    expenseDateKeyYmd(r.date),
    String(r.vehicleNumber ?? "").trim().toUpperCase(),
    amtNorm,
    String(r.category ?? "").trim(),
  ].join("|");
}

function displayMaintenancePickerAmount(rec) {
  if (rec == null) return null;
  if (String(rec.maintenanceType || "").trim() === "Loan") {
    const la = rec.loanAmount;
    if (la != null && la !== "") {
      const n = Number(la);
      return Number.isFinite(n) ? n : null;
    }
  }
  if (rec.amount != null && rec.amount !== "") {
    const n = Number(rec.amount);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Manual expenses from API plus truck-maintenance rows (when not already duplicated manually). */
function mergeVehicleExpensesWithMaintenance(apiRows = [], tmRows = []) {
  const manual = (Array.isArray(apiRows) ? apiRows : []).filter(
    (r) => String(r.expenseType || "Vehicle Expense").trim() === "Vehicle Expense"
  );

  const seen = new Set();
  manual.forEach((r) => seen.add(vehicleExpenseFingerprint(r)));

  const augmented = [];
  for (const rec of Array.isArray(tmRows) ? tmRows : []) {
    const mapped = mapTruckMaintenanceToExpense(rec);
    const cat = String(mapped.category || "").trim();
    if (!VEHICLE_EXPENSE_CATEGORIES.includes(cat)) continue;
    if (!String(mapped.vehicleNumber || "").trim()) continue;

    const amountNum = parseFloat(String(mapped.amount ?? "").replace(/,/g, ""));
    if (!Number.isFinite(amountNum) || amountNum <= 0) continue;

    const txn = (mapped.paymentReference || "").trim();
    const tmp = {
      ...mapped,
      expenseType: "Vehicle Expense",
      amount: amountNum,
      date: mapped.date,
      paidBy: (mapped.paidBy || "").trim() || "",
      paymentReference: txn ? `${txn} · Truck Maintenance` : "Truck Maintenance",
      __fromMaintenance: true,
      __maintId: rec._id != null ? String(rec._id) : null,
    };

    const fp = vehicleExpenseFingerprint(tmp);
    if (seen.has(fp)) continue;
    seen.add(fp);
    augmented.push(tmp);
  }

  const sortTs = (d) => {
    const key = expenseDateKeyYmd(d);
    const t = key ? Date.parse(`${key}T12:00:00`) : 0;
    return Number.isFinite(t) ? t : 0;
  };

  return [...manual, ...augmented].sort((a, b) => sortTs(b.date) - sortTs(a.date));
}

const Expenses = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [apiExpenses, setApiExpenses] = useState([]);
  const [truckMaintenanceRecords, setTruckMaintenanceRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const emptyForm = {
    expenseType: "Vehicle Expense",
    date: new Date().toISOString().split("T")[0],
    vehicleNumber: "",
    description: "",
    amount: "",
    requestedBy: "",
    paidBy: "",
    paymentMethod: "Cash",
    paymentReference: "",
    vendor: "",
    category: "",
    checkAmount: "",
  };
  const [form, setForm] = useState(emptyForm);
  /** { key, rec } rows for modal — Truck Maintenance prefilled picker */
  const [maintenancePickOptions, setMaintenancePickOptions] = useState([]);
  const [selectedMaintKey, setSelectedMaintKey] = useState("");
  /** Only while the add modal is loading maintenance rows (not the whole page). */
  const [modalPickLoading, setModalPickLoading] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const refreshExpensesPageData = async () => {
    /* Fetch separately: /vehicle/expenses uses auth; truck-maintenance does not. If expenses fails,
       we still show truck-maintenance-derived rows instead of wiping both via Promise.all + catch. */
    let expenseRows = [];
    try {
      const expRes = await axios.get(`${BASE_URL}/vehicle/expenses`, { withCredentials: true });
      expenseRows = Array.isArray(expRes.data) ? expRes.data : [];
    } catch {
      expenseRows = [];
    }
    setApiExpenses(expenseRows);

    let tmRows = [];
    try {
      const tmRes = await axios.get(`${BASE_URL}/vehicle/truck-maintenance`, { withCredentials: true });
      tmRows = Array.isArray(tmRes.data) ? tmRes.data : [];
    } catch {
      tmRows = [];
    }
    setTruckMaintenanceRecords(tmRows);
  };

  useEffect(() => {
    void refreshExpensesPageData();
  }, []);

  const combinedVehicleExpenseRows = useMemo(
    () => mergeVehicleExpensesWithMaintenance(apiExpenses, truckMaintenanceRecords),
    [apiExpenses, truckMaintenanceRecords],
  );

  const isOthersTab = activeTab === "Others";
  const isVehicleView = !isOthersTab;

  const filtered = useMemo(() => {
    if (isOthersTab) {
      return apiExpenses.filter((r) => (r.expenseType || "").trim() === "Others");
    }
    const pool = combinedVehicleExpenseRows;
    if (activeTab === "All") return pool;
    return pool.filter((r) => String(r.category || "").trim() === activeTab);
  }, [isOthersTab, activeTab, apiExpenses, combinedVehicleExpenseRows]);

  const chequeStats = useMemo(() => {
    const pool = isOthersTab ? apiExpenses : combinedVehicleExpenseRows;
    const rows = (Array.isArray(pool) ? pool : []).filter(
      (r) => String(r.paymentMethod || "").trim().toLowerCase() === "cheque",
    );
    const todayYmd = new Date().toISOString().split("T")[0];
    const monthPref = new Date().toISOString().slice(0, 7);
    let day = 0;
    let month = 0;
    for (const r of rows) {
      const ymd = expenseDateKeyYmd(r.date);
      const chk = parseFloat(r.checkAmount);
      const amt = parseFloat(r.amount);
      const n = Number.isFinite(chk) && chk > 0 ? chk : Number.isFinite(amt) ? amt : 0;
      if (ymd === todayYmd) day += n;
      if (ymd && ymd.slice(0, 7) === monthPref) month += n;
    }
    return { day, month };
  }, [isOthersTab, apiExpenses, combinedVehicleExpenseRows]);

  const vehicleExpenseBaseForm = () => ({
    ...emptyForm,
    expenseType: "Vehicle Expense",
    category: activeTab === "All" ? "" : activeTab,
  });

  const openAddExpenseModal = async () => {
    setMaintenancePickOptions([]);
    setSelectedMaintKey("");

    if (isOthersTab) {
      setForm({ ...emptyForm, expenseType: "Others", category: "" });
      setShowForm(true);
      return;
    }

    const base = vehicleExpenseBaseForm();
    setForm(base);
    setShowForm(true);

    try {
      let pool = [...truckMaintenanceRecords];
      if (pool.length === 0) {
        setModalPickLoading(true);
        try {
          const res = await axios.get(`${BASE_URL}/vehicle/truck-maintenance`, { withCredentials: true });
          pool = Array.isArray(res.data) ? res.data : [];
          setTruckMaintenanceRecords(pool);
        } finally {
          setModalPickLoading(false);
        }
      }

      const filteredPool =
        activeTab === "All"
          ? pool
          : pool.filter((r) => String(r.maintenanceType || "").trim() === activeTab);
      const sorted = [...filteredPool].sort((a, b) => {
        const ta = new Date(a.date || 0).getTime();
        const tb = new Date(b.date || 0).getTime();
        return tb - ta;
      });
      const keyed = sorted.map((rec, idx) => ({
        key: truckMaintPickKey(rec, idx),
        rec,
      }));
      setMaintenancePickOptions(keyed);

      if (keyed.length === 0) return;

      const first = keyed[0];
      setSelectedMaintKey(first.key);
      const mapped = mapTruckMaintenanceToExpense(first.rec);
      const cat =
        activeTab === "All"
          ? (VEHICLE_EXPENSE_CATEGORIES.includes(mapped.category) ? mapped.category : "")
          : activeTab;
      setForm((prev) => ({
        ...prev,
        ...mapped,
        expenseType: "Vehicle Expense",
        category: cat,
      }));
    } catch {
      setModalPickLoading(false);
      /* keep manual empty form */
    }
  };

  const applyMaintenancePickKey = (key) => {
    setSelectedMaintKey(key);
    if (!key) {
      setForm(vehicleExpenseBaseForm());
      return;
    }
    const item = maintenancePickOptions.find((o) => o.key === key);
    if (!item) return;
    const mapped = mapTruckMaintenanceToExpense(item.rec);
    const cat =
      activeTab === "All"
        ? (VEHICLE_EXPENSE_CATEGORIES.includes(mapped.category) ? mapped.category : "")
        : activeTab;
    setForm((prev) => ({
      ...prev,
      ...mapped,
      expenseType: "Vehicle Expense",
      category: cat,
    }));
  };

  const handleSave = async () => {
    const payload = { ...form };
    if (isOthersTab) {
      payload.expenseType = "Others";
      if (!String(payload.category || "").trim()) {
        alert("Please select a category.");
        return;
      }
    } else {
      payload.expenseType = "Vehicle Expense";
      const cat = (activeTab === "All" ? form.category : activeTab).trim();
      if (!cat) {
        alert("Please select an expense category (maintenance type).");
        return;
      }
      payload.category = cat;
    }
    try {
      const nCheck = parseFloat(String(payload.checkAmount ?? "").replace(/,/g, ""));
      if (Number.isFinite(nCheck) && nCheck >= 0) payload.checkAmount = nCheck;
      else delete payload.checkAmount;

      await axios.post(`${BASE_URL}/vehicle/expenses`, payload, { withCredentials: true });
      setSuccessMsg("Expense saved successfully!");
      setMaintenancePickOptions([]);
      setSelectedMaintKey("");
      setForm({ ...emptyForm, expenseType: "Vehicle Expense", category: isOthersTab ? "" : (activeTab === "All" ? "" : activeTab) });
      setShowForm(false);
      void refreshExpensesPageData();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch { alert("Save failed — check backend connection."); }
  };

  const total = filtered.reduce((a, r) => a + parseFloat(r.amount || 0), 0);
  const expenseRecordColCount = isOthersTab ? 9 : 11;

  const S = {
    container: {
      ...vmPageShell(isMobile, { marginRightPx: isMobile ? 0 : 10 }),
      fontFamily: "Arial, sans-serif",
      backgroundColor: "white",
      color: "black",
      minHeight: "calc(100vh - 70px)",
      boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    },
    pageHeader: {
      backgroundColor: "#FFC107",
      color: "#000",
      padding: "16px 20px",
      fontWeight: "bold",
      fontSize: "22px",
      letterSpacing: "1px",
      textTransform: "uppercase",
    },
    innerPad: {
      padding: isMobile ? "14px" : "20px",
    },
    topRow: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: "14px",
    },
    addBtn: {
      backgroundColor: "#FFC107",
      color: "#000",
      border: "none",
      padding: "9px 20px",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "bold",
      fontSize: "14px",
    },
    tabRow: {
      display: "flex",
      borderBottom: "2px solid #e0a800",
      marginBottom: "16px",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      flexWrap: "nowrap",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    },
    tab: (active) => ({
      padding: isMobile ? "9px 10px" : "10px 18px",
      cursor: "pointer",
      fontSize: isMobile ? "11px" : "14px",
      fontWeight: active ? "bold" : "normal",
      color: active ? "#000" : "#555",
      backgroundColor: active ? "#FFC107" : "transparent",
      border: "none",
      outline: "none",
      borderRadius: "4px 4px 0 0",
      whiteSpace: "nowrap",
      flexShrink: 0,
      fontFamily: "Arial, sans-serif",
    }),
    summaryRow: {
      display: "flex",
      gap: "14px",
      marginBottom: "16px",
      flexWrap: "wrap",
    },
    summaryCard: (bg) => ({
      flex: 1,
      padding: "14px 18px",
      borderRadius: "6px",
      backgroundColor: bg,
      border: "1.5px solid #000",
    }),
    summaryValue: {
      fontSize: "22px",
      fontWeight: "bold",
      color: "#000",
    },
    summaryLabel: {
      fontSize: "13px",
      color: "#555",
      marginTop: "4px",
    },
    success: {
      backgroundColor: "#e6f9ee",
      color: "#27ae60",
      padding: "10px 16px",
      borderRadius: "4px",
      marginBottom: "12px",
      fontSize: "14px",
      border: "1px solid #27ae60",
    },
    table: getVmRecordTableStyle(isMobile, expenseRecordColCount),
    th: getVmRecordThStyle(isMobile),
    td: { ...getVmRecordTdStyle(isMobile), color: "#000" },
    // Modal styles
    overlay: {
      position: "fixed",
      top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.4)",
      zIndex: 1000,
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      paddingTop: "60px",
    },
    modal: {
      background: "#fff",
      borderRadius: "8px",
      padding: "24px",
      width: "700px",
      maxHeight: "80vh",
      overflowY: "auto",
      boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
    },
    modalHeader: {
      backgroundColor: "#FFC107",
      color: "#000",
      padding: "12px 16px",
      fontWeight: "bold",
      fontSize: "16px",
      borderRadius: "6px",
      marginBottom: "20px",
      textTransform: "uppercase",
      letterSpacing: "1px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "14px",
    },
    group: {
      display: "flex",
      flexDirection: "column",
      gap: "5px",
    },
    label: {
      fontSize: "13px",
      fontWeight: "bold",
      color: "#000",
    },
    input: {
      padding: "8px 10px",
      border: "1.5px solid #000",
      borderRadius: "4px",
      fontSize: "14px",
      color: "#000",
      backgroundColor: "#fff",
      outline: "none",
    },
    select: {
      padding: "8px 10px",
      border: "1.5px solid #000",
      borderRadius: "4px",
      fontSize: "14px",
      color: "#000",
      backgroundColor: "#fff",
      outline: "none",
    },
    btnRow: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "10px",
      marginTop: "20px",
    },
    saveBtn: {
      backgroundColor: "#FFC107",
      color: "#000",
      border: "none",
      padding: "9px 24px",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "bold",
      fontSize: "14px",
    },
    clearBtn: {
      backgroundColor: "#000",
      color: "#fff",
      border: "none",
      padding: "9px 24px",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "bold",
      fontSize: "14px",
    },
  };

  const ch = (e) =>
    setForm((f) => {
      const { name, value } = e.target;
      const next = { ...f, [name]: value };
      if (name === "paymentMethod" && value !== "Cheque") next.checkAmount = "";
      return next;
    });

  return (
    <div style={S.container}>
      <div style={S.pageHeader}>EXPENSES</div>

      <div style={S.innerPad}>

        {successMsg && <div style={S.success}>✅ {successMsg}</div>}

        <div style={S.topRow}>
          <button type="button" style={S.addBtn} onClick={openAddExpenseModal}>
            + Add Expense
          </button>
        </div>

        <div style={S.tabRow}>
          {EXPENSE_TABS.map((t) => (
            <button key={t} type="button" style={S.tab(activeTab === t)} onClick={() => setActiveTab(t)}>
              {t}
            </button>
          ))}
        </div>

        <div style={S.summaryRow}>
          <div style={S.summaryCard("#fff8e1")}>
            <div style={S.summaryValue}>₹{total.toLocaleString("en-IN")}</div>
            <div style={S.summaryLabel}>Total {activeTab}</div>
          </div>
          <div style={S.summaryCard("#f0f0f0")}>
            <div style={S.summaryValue}>{filtered.length}</div>
            <div style={S.summaryLabel}>Entries</div>
          </div>
          {isVehicleView && (
            <div style={S.summaryCard("#e8f5e9")}>
              <div style={S.summaryValue}>
                {new Set(filtered.map(r => r.vehicleNumber).filter(Boolean)).size}
              </div>
              <div style={S.summaryLabel}>Vehicles</div>
            </div>
          )}
          <div style={S.summaryCard("#e3f2fd")}>
            <div style={S.summaryValue}>₹{chequeStats.day.toLocaleString("en-IN")}</div>
            <div style={S.summaryLabel}>Cheque (today)</div>
          </div>
          <div style={S.summaryCard("#fce4ec")}>
            <div style={S.summaryValue}>₹{chequeStats.month.toLocaleString("en-IN")}</div>
            <div style={S.summaryLabel}>Cheque (this month)</div>
          </div>
        </div>

        <div style={vmTableScrollWrap}>
          <table style={S.table}>
            {isMobile ? vmRecordColGroupReact(expenseRecordColCount) : null}
            <thead>
              <tr>
                {isVehicleView
                  ? ["Date", "Vehicle", "Category", "Description", "Vendor", "Amount", "Check (₹)", "Requested By", "Paid By", "Method", "Reference"].map((h, hi) => (
                      <th style={S.th} key={hi}>{h}</th>
                    ))
                  : ["Date", "Category", "Description", "Amount", "Check (₹)", "Requested By", "Paid By", "Method", "Reference"].map((h, hi) => (
                      <th style={S.th} key={hi}>{h}</th>
                    ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((r, i) => (
                <tr
                  key={
                    r.__maintId != null
                      ? `tm-${r.__maintId}`
                      : r._id != null
                        ? `ex-${String(r._id)}`
                        : `row-${vehicleExpenseFingerprint(r)}-${i}`
                  }
                  style={{ backgroundColor: vmRecordRowBg(i) }}
                >
                  <td style={S.td}>{r.date ? new Date(r.date).toLocaleDateString("en-IN") : ""}</td>
                  {isVehicleView && <td style={S.td}>{r.vehicleNumber || "—"}</td>}
                  {isVehicleView && <td style={S.td}>{(r.category || "").trim() || "—"}</td>}
                  {isOthersTab && <td style={S.td}>{r.category}</td>}
                  <td style={S.td}>{r.description}</td>
                  {isVehicleView && <td style={S.td}>{r.vendor || "—"}</td>}
                  <td style={S.td}><b>₹{parseFloat(r.amount || 0).toLocaleString("en-IN")}</b></td>
                  <td style={S.td}>
                    {String(r.paymentMethod || "").trim().toLowerCase() === "cheque" ? (
                      <b>
                        ₹
                        {(Number.isFinite(parseFloat(r.checkAmount)) && parseFloat(r.checkAmount) > 0
                          ? parseFloat(r.checkAmount)
                          : parseFloat(r.amount || 0)
                        ).toLocaleString("en-IN")}
                      </b>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={S.td}>{r.requestedBy}</td>
                  <td style={S.td}>{r.paidBy}</td>
                  <td style={S.td}>{r.paymentMethod}</td>
                  <td style={S.td}>{r.paymentReference}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={expenseRecordColCount} style={{ ...S.td, textAlign: "center", color: "#aaa", padding: "28px" }}>
                    No {activeTab} records. Click "+ Add Expense" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={S.modal}>
            <div style={S.modalHeader}>
              ADD {isOthersTab ? "OTHER EXPENSE" : "VEHICLE EXPENSE"}
              {!isOthersTab && activeTab !== "All" ? ` — ${activeTab}` : ""}
            </div>
            <div style={S.grid}>
              {isVehicleView && modalPickLoading && (
                <div style={{ gridColumn: "1 / -1", fontSize: "13px", color: "#555" }}>
                  Loading Truck Maintenance list…
                </div>
              )}
              {isVehicleView && maintenancePickOptions.length > 0 && (
                <div style={{ ...S.group, gridColumn: "1/-1" }}>
                  <label style={S.label}>Copy from Truck Maintenance</label>
                  <select
                    style={S.select}
                    value={selectedMaintKey}
                    onChange={(e) => applyMaintenancePickKey(e.target.value)}
                  >
                    <option value="">— Clear (manual entry) —</option>
                    {maintenancePickOptions.map(({ key, rec }) => {
                      const amtPick = displayMaintenancePickerAmount(rec);
                      const dateShown =
                        typeof rec.date === "string"
                          ? rec.date.slice(0, 10)
                          : rec.date
                            ? new Date(rec.date).toLocaleDateString("en-IN")
                            : "?";
                      return (
                        <option key={key} value={key}>
                          {dateShown} · {rec.truckNo || "?"}
                          {" · "}₹{amtPick != null ? amtPick.toLocaleString("en-IN") : "—"}
                          {" · "}
                          {String(rec.maintenanceType || "").slice(0, 24)}
                        </option>
                      );
                    })}
                  </select>
                  <span style={{ fontSize: "11px", color: "#555", marginTop: "4px" }}>
                    Opens with the latest matching maintenance row; choose another row or clear to edit freely.
                  </span>
                </div>
              )}
              <div style={S.group}>
                <label style={S.label}>Date</label>
                <input style={S.input} type="date" name="date" value={form.date} onChange={ch} />
              </div>
              {isVehicleView ? (
                <>
                  <div style={S.group}>
                    <label style={S.label}>Vehicle Number</label>
                    <FleetVehicleSelect
                      name="vehicleNumber"
                      value={form.vehicleNumber}
                      onChange={ch}
                      style={S.select}
                      placeholderOption="Select Vehicle"
                    />
                  </div>
                  {activeTab === "All" && (
                    <div style={S.group}>
                      <label style={S.label}>Expense category</label>
                      <select style={S.select} name="category" value={form.category} onChange={ch}>
                        <option value="">Select type (same as Truck Maintenance)</option>
                        {VEHICLE_EXPENSE_CATEGORIES.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <div style={S.group}>
                  <label style={S.label}>Category</label>
                  <select style={S.select} name="category" value={form.category} onChange={ch}>
                    <option value="">Select</option>
                    {["Office Expenses", "Staff Welfare", "Courier", "Utilities", "Printing", "Miscellaneous"]
                      .map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              )}
              <div style={{ ...S.group, gridColumn: "1/-1" }}>
                <label style={S.label}>Description</label>
                <input style={S.input} name="description" value={form.description} onChange={ch} placeholder="Enter description" />
              </div>
              {isVehicleView && (
                <div style={S.group}>
                  <label style={S.label}>Vendor / Supplier</label>
                  <input style={S.input} name="vendor" value={form.vendor} onChange={ch} placeholder="Vendor name" />
                </div>
              )}
              <div style={S.group}>
                <label style={S.label}>Amount (₹)</label>
                <input style={S.input} type="number" name="amount" value={form.amount} onChange={ch} placeholder="0" />
              </div>
              <div style={S.group}>
                <label style={S.label}>Requested By</label>
                <input style={S.input} name="requestedBy" value={form.requestedBy} onChange={ch} />
              </div>
              <div style={S.group}>
                <label style={S.label}>Paid By</label>
                <input style={S.input} name="paidBy" value={form.paidBy} onChange={ch} />
              </div>
              <div style={S.group}>
                <label style={S.label}>Payment Method</label>
                <select style={S.select} name="paymentMethod" value={form.paymentMethod} onChange={ch}>
                  {["Cash", "Bank Transfer", "UPI", "Cheque", "Card"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              {form.paymentMethod === "Cheque" && (
                <div style={S.group}>
                  <label style={S.label}>Cheque amount (₹)</label>
                  <input
                    style={S.input}
                    type="number"
                    name="checkAmount"
                    value={form.checkAmount}
                    onChange={ch}
                    placeholder="Same as amount if left blank"
                  />
                </div>
              )}
              <div style={S.group}>
                <label style={S.label}>Payment Reference</label>
                <input style={S.input} name="paymentReference" value={form.paymentReference} onChange={ch} placeholder="UTR / Cheque No" />
              </div>
            </div>
            <div style={S.btnRow}>
              <button
                type="button"
                style={S.clearBtn}
                onClick={() => {
                  setShowForm(false);
                  setMaintenancePickOptions([]);
                  setSelectedMaintKey("");
                  setForm({
                    ...emptyForm,
                    expenseType: isOthersTab ? "Others" : "Vehicle Expense",
                    category: isOthersTab ? "" : (activeTab === "All" ? "" : activeTab),
                  });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                style={S.clearBtn}
                onClick={() => setForm({
                  ...emptyForm,
                  expenseType: isOthersTab ? "Others" : "Vehicle Expense",
                  category: isOthersTab ? "" : (activeTab === "All" ? "" : activeTab),
                })}
              >
                Clear
              </button>
              <button type="button" style={S.saveBtn} onClick={handleSave}>Save Expense</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;